import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./PostmarkPaywall.module.scss"

/**
 * PostmarkPaywall (`/postmark`) — a self-contained EXPIRATION / RENEWAL paywall
 * for Private Photo Vault, staged as an ARCHIVIST'S DESK. The year's memories are
 * mounted on a warm archive page; a big rubber DATE-STAMP rests above it. You
 * press the stamp down and it thunks a circular blue-ink POSTMARK across the page
 * — "★ PRIVATE PHOTO VAULT ★ · KEPT THROUGH ‹Mon Year›" — certifying your vault
 * is protected through a real future date. Each memory gains a small KEPT ✓ seal
 * and the amber "backup pauses when your plan ends" tab settles to a blue
 * "kept & protected" plate.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/postmark.md
 *
 * Mechanic (new to the ledger): PRESS-A-STAMP — a discrete IMPACT / IMPRESSION.
 * Drag the stamp down (or tap it / press "Stamp it kept" / Enter-Space); past the
 * threshold it commits with a thunk (a tiny screen shake) and an INK-BLEED (the
 * postmark blurs in then sharpens, like wet ink hitting paper), then springs back
 * up. Distinct from every prior scrub / hold / flip / toss / paint / dial / riffle /
 * node-graph / draw-a-link / lever-pull / stitch / lasso / place-a-piece / rope-haul /
 * key-wind / raise-resin / comparison-wipe.
 *
 * Every ledger axis is chosen to differ from the 10 prior expiration concepts:
 *   - Layout:      an archival ALBUM / MOUNT PAGE overprinted by one large circular
 *                  POSTMARK (photos mounted with corners on warm card + a stamp that
 *                  prints across them) — not Deep Freeze's hero card, Unbroken's
 *                  calendar grid, Ahead's road, Keystone's arch, Keep Posted's flap
 *                  board, Patchwork's quilt, Evergreen's wheel, Keepsake's card deck,
 *                  Beacon's harbour or Mainspring's music box.
 *   - Interaction: PRESS-A-STAMP impact / impression (see above).
 *   - Register:    ASSURED, satisfying, warm-official — "make it official / certify
 *                  it kept" (a playful thunk). Not Deep Freeze cold-fear, Unbroken
 *                  earned-pride, Ahead hopeful-road, Keystone load-bearing, Keep
 *                  Posted retro-clacky, Patchwork handmade-textile, Evergreen
 *                  nurturing-seasonal, Keepsake collector-tender, Beacon steadfast-
 *                  vigil or Mainspring mechanical-craft.
 *   - Art:        LIGHT warm card-stock / archival paper + PHOTOGRAPHIC memory prints
 *                  + brand-blue INK (postmark, kept-seals, franking lines); amber =
 *                  the paused / expiring state. No dark theme, no skeuomorphic UI.
 *   - Value:      COMMITMENT + CERTIFICATION for RENEWAL — your year is already saved;
 *                  you actively certify continued protection. Honest loss-aversion
 *                  (backup PAUSES when a plan ends — nothing is deleted).
 *
 * Honest & App Store compliant: the stamp only marks how far protection is PAID
 * THROUGH — photos never leave the vault and nothing is ever deleted (copy says so).
 * The coverage date is the real today + the plan's term, computed live; Save % is
 * exact yearly-vs-monthly arithmetic; there is no fake countdown or scarcity.
 * Purchase is never gated on the demo. Yearly is the featured, default plan and
 * carries the most prominent price; monthly is small and secondary. Memory prints
 * are bundled demo photos from `public/recall/` (no network egress).
 */

type PlanKey = "yearly" | "monthly"

// this year's memories, mounted on the archive page (bundled demo photos)
const MEMORIES = [
  { src: "/recall/sunset-1.jpg", rot: -3.2 },
  { src: "/recall/beach-2.jpg", rot: 2.4 },
  { src: "/recall/mountain-1.jpg", rot: -1.8 },
  { src: "/recall/coffee-1.jpg", rot: 2.8 },
  { src: "/recall/dog-2.jpg", rot: -2.4 },
  { src: "/recall/flower-2.jpg", rot: 1.9 },
  { src: "/recall/food-1.jpg", rot: -2.8 },
  { src: "/recall/city-2.jpg", rot: 2.2 },
  { src: "/recall/mountain-3.jpg", rot: -1.6 },
]
const TOTAL = MEMORIES.length

const PRESS_TRAVEL = 118 // px of downward drag that fully presses the stamp
const COMMIT_AT = 0.82 // fraction of travel past which the stamp commits
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// ── the postmark impression (circular date-stamp) ───────────────────────────────
const Postmark = ({ date }: { date: string }) => (
  <svg viewBox="0 0 200 200" className={styles.pmSvg} aria-hidden>
    <defs>
      <path id="pmTop" d="M 26 100 A 74 74 0 0 1 174 100" />
      <path id="pmBot" d="M 30 100 A 70 70 0 0 0 170 100" />
    </defs>
    {/* rings */}
    <circle cx="100" cy="100" r="88" className={styles.pmRingOuter} />
    <circle cx="100" cy="100" r="80" className={styles.pmRingInner} />
    {/* curved legends */}
    <text className={styles.pmArc}>
      <textPath href="#pmTop" startOffset="50%" textAnchor="middle">
        ★ PRIVATE PHOTO VAULT ★
      </textPath>
    </text>
    <text className={styles.pmArcSmall}>
      <textPath href="#pmBot" startOffset="50%" textAnchor="middle">
        KEPT · PROTECTED · IN SYNC
      </textPath>
    </text>
    {/* centre date band */}
    <line x1="40" y1="80" x2="160" y2="80" className={styles.pmRule} />
    <line x1="40" y1="122" x2="160" y2="122" className={styles.pmRule} />
    <text x="100" y="93" textAnchor="middle" className={styles.pmKept}>
      KEPT THROUGH
    </text>
    <text x="100" y="115" textAnchor="middle" className={styles.pmDate}>
      {date}
    </text>
  </svg>
)

// franking / cancellation lines that fan off the postmark
const Frank = () => (
  <svg viewBox="0 0 150 90" className={styles.frankSvg} aria-hidden>
    {[18, 32, 46, 60, 74].map((y) => (
      <path
        key={y}
        d={`M 4 ${y} q 18 -8 36 0 t 36 0 t 36 0 t 36 0`}
        className={styles.frankLine}
      />
    ))}
  </svg>
)

// ── tiny inline icons ───────────────────────────────────────────────────────────
const Tick = ({ size = 11 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const Lock = ({ size = 13 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <rect
      x={4.5}
      y={10.5}
      width={15}
      height={10}
      rx={2.6}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
    />
    <path
      d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
  </svg>
)

const PostmarkPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (real debug data; yearly featured) ──────────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // the real coverage date if you renew now — today + the plan's term
  const coverThrough = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + (plan === "yearly" ? 12 : 1))
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
  }, [plan])
  const coverLong = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + (plan === "yearly" ? 12 : 1))
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" })
  }, [plan])

  // ── stamp state ───────────────────────────────────────────────────────────────
  const [press, setPress] = useState(0) // live 0 (up) → 1 (fully down)
  const [stamped, setStamped] = useState(false)
  const [handed, setHanded] = useState(false) // teaching bob done
  const [shake, setShake] = useState(false)
  const [reink, setReink] = useState(false)

  const draggingRef = useRef(false)
  const startYRef = useRef(0)
  const startPressRef = useRef(0)
  const movedRef = useRef(false)
  const rafRef = useRef<number | null>(null)
  const demoRef = useRef<number | null>(null)
  const stampedRef = useRef(false)
  stampedRef.current = stamped

  const stopRaf = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
  }
  const stopDemo = () => {
    if (demoRef.current) window.clearTimeout(demoRef.current)
    demoRef.current = null
  }

  // tween the press value (used by the auto press-and-lift + teaching bob)
  const tween = useCallback(
    (from: number, to: number, ms: number, onDone?: () => void) => {
      stopRaf()
      let start: number | null = null
      const overshoot = to === 0 // add a little bounce on the way up
      const step = (t: number) => {
        if (start === null) start = t
        const k = clamp((t - start) / ms, 0, 1)
        let eased = to > from ? 1 - Math.pow(1 - k, 2) : k * k * (3 - 2 * k) // in / smooth
        let v = from + (to - from) * eased
        if (overshoot && k > 0.6 && k < 1) v -= Math.sin((k - 0.6) * 7) * 0.05
        setPress(clamp(v, 0, 1))
        if (k < 1) rafRef.current = requestAnimationFrame(step)
        else {
          rafRef.current = null
          onDone?.()
        }
      }
      rafRef.current = requestAnimationFrame(step)
    },
    [],
  )

  const commit = useCallback(() => {
    if (!stampedRef.current) {
      setStamped(true)
      setShake(true)
      window.setTimeout(() => setShake(false), 360)
      native.logMixpanelEvent("html_postmark_stamped", { plan })
    }
  }, [plan])

  // full programmatic press: down → thunk → up
  const pressAndStamp = useCallback(() => {
    stopDemo()
    setHanded(true)
    tween(press, 1, 150, () => {
      commit()
      tween(1, 0, 340)
    })
  }, [press, tween, commit])

  // ── teaching bob: dip the stamp partway, then hand it over ─────────────────────
  useEffect(() => {
    if (reduceMotion) {
      setStamped(true)
      setHanded(true)
      return
    }
    demoRef.current = window.setTimeout(() => {
      tween(0, 0.44, 480, () => tween(0.44, 0, 420, () => setHanded(true)))
    }, 900)
    return () => {
      stopDemo()
      stopRaf()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // re-ink when the plan (and therefore the stamped date) changes after stamping
  useEffect(() => {
    if (!stamped) return
    setReink(true)
    const id = window.setTimeout(() => setReink(false), 460)
    return () => window.clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverThrough])

  // ── pointer: press the stamp yourself ──────────────────────────────────────────
  const onDown = (e: React.PointerEvent) => {
    stopDemo()
    stopRaf()
    setHanded(true)
    draggingRef.current = true
    movedRef.current = false
    startYRef.current = e.clientY
    startPressRef.current = press
    ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
  }
  const onMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    const dy = e.clientY - startYRef.current
    if (Math.abs(dy) > 3) movedRef.current = true
    const p = clamp(startPressRef.current + dy / PRESS_TRAVEL, 0, 1)
    setPress(p)
    if (p >= 0.999) {
      commit()
    }
  }
  const onUp = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    if (!movedRef.current) {
      // treated as a tap
      pressAndStamp()
      return
    }
    if (press >= COMMIT_AT) {
      tween(press, 1, 90, () => {
        commit()
        tween(1, 0, 340)
      })
    } else {
      tween(press, 0, 260)
    }
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      pressAndStamp()
    }
  }

  const restamp = () => {
    stopDemo()
    stopRaf()
    setStamped(false)
    setPress(0)
    setHanded(true)
    native.logMixpanelEvent("html_postmark_reset", {})
  }

  // ── plan + purchase wiring ─────────────────────────────────────────────────────
  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // ── copy ───────────────────────────────────────────────────────────────────────
  const headline = stamped
    ? `Kept through ${coverLong}.`
    : "Keep your year on the record."
  const sub = stamped
    ? "Every original — full quality, private and encrypted, backed up automatically and in sync on every device. Nothing is ever deleted; your photos are always yours."
    : "Every memory you saved this year is already safe in your vault. Press the stamp to keep it that way — for the year ahead."

  const hint = stamped
    ? "Stamped. This is the protection Premium keeps for you — automatically, every single day."
    : !handed
      ? "Inking the stamp…"
      : press > 0.05
        ? "Press it all the way down…"
        : "Press the date-stamp onto your year to certify it kept."

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? `Start ${trialDays} days free`
        : "Keep it kept — Yearly"
      : "Choose monthly"
  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  const stageVars = {
    ["--press" as any]: press.toFixed(3),
  } as React.CSSProperties

  return (
    <div className={styles.page}>
      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandKey} aria-hidden>
            <Lock size={12} />
          </span>
          <span>Private Photo Vault</span>
          <span className={styles.premiumPip}>Premium</span>
        </div>
        <h1 className={`${styles.title} ${stamped ? styles.titleDone : ""}`}>
          {headline}
        </h1>
        <p className={styles.sub}>{sub}</p>
      </header>

      {/* ── the archivist's page ─────────────────────────────────────────────── */}
      <div
        className={`${styles.stage} ${stamped ? styles.stageDone : ""} ${
          shake ? styles.shake : ""
        }`}
        style={stageVars}
      >
        <div className={styles.pageHead}>
          <span className={styles.pageTitle}>YOUR YEAR IN MEMORIES</span>
          <span
            className={`${styles.statusTab} ${stamped ? styles.statusOn : ""}`}
          >
            {stamped ? (
              <>
                <Tick size={10} /> KEPT THROUGH {coverThrough.toUpperCase()}
              </>
            ) : (
              <>BACKUP PAUSES WHEN YOUR PLAN ENDS</>
            )}
          </span>
        </div>

        {/* mounted memory prints */}
        <div className={styles.mount}>
          {MEMORIES.map((m, i) => (
            <span
              key={m.src}
              className={styles.photo}
              style={{
                ["--rot" as any]: `${m.rot}deg`,
                ["--i" as any]: i,
              }}
            >
              <img
                src={m.src}
                alt=""
                draggable={false}
                onError={(e) =>
                  ((e.currentTarget as HTMLImageElement).style.opacity = "0")
                }
              />
              <span className={styles.corner} data-c="tl" aria-hidden />
              <span className={styles.corner} data-c="br" aria-hidden />
              <span className={styles.keptSeal} aria-hidden>
                <Tick size={9} />
              </span>
            </span>
          ))}

          {/* the postmark, printed on commit */}
          <span
            className={`${styles.postmark} ${stamped ? styles.postmarkOn : ""} ${
              reink ? styles.postmarkReink : ""
            }`}
            aria-hidden
          >
            <Postmark date={coverThrough.toUpperCase()} />
          </span>
          <span
            className={`${styles.frank} ${stamped ? styles.frankOn : ""}`}
            aria-hidden
          >
            <Frank />
          </span>
        </div>

        {/* the rubber date-stamp you press */}
        <div
          className={styles.stamp}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onKeyDown={onKeyDown}
          role="button"
          tabIndex={0}
          aria-label={`Press the date-stamp to keep your vault protected through ${coverLong}`}
        >
          <span className={styles.stampHandle} aria-hidden />
          <span className={styles.stampNeck} aria-hidden />
          <span className={styles.stampCollar} aria-hidden />
          <span className={styles.stampDie} aria-hidden>
            <span className={styles.stampDieDate}>
              {coverThrough.toUpperCase()}
            </span>
            <span className={styles.stampDieLabel}>KEPT</span>
          </span>
          <span className={styles.stampShadow} aria-hidden />
          {!stamped && handed && press < 0.05 && (
            <span className={styles.pressCue} aria-hidden>
              PRESS
            </span>
          )}
        </div>
      </div>

      <p
        className={`${styles.hintLine} ${stamped ? styles.hintDone : ""}`}
        aria-live="polite"
      >
        {hint}
      </p>

      <div className={styles.tools}>
        <button
          className={styles.toolPrimary}
          onClick={pressAndStamp}
          disabled={stamped}
        >
          {stamped ? (
            <>
              <Tick size={13} /> Stamped kept
            </>
          ) : (
            "Stamp it kept"
          )}
        </button>
        <button
          className={styles.toolGhost}
          onClick={restamp}
          disabled={!stamped}
        >
          Re-date
        </button>
      </div>

      {/* what "kept" means — the honest Premium bundle */}
      <ul className={styles.keeps}>
        {[
          "Full-quality originals — backed up automatically",
          "Private & encrypted — only your passcode opens the vault",
          "In sync on every device — your whole library, everywhere",
        ].map((t) => (
          <li key={t}>
            <span className={styles.keepDot} aria-hidden>
              <Tick size={10} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── plans — yearly featured, monthly small & secondary ───────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${
            plan === "yearly" ? styles.planSel : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planMain}>
            <span className={styles.planName}>Yearly</span>
            <span className={styles.planNote}>Stamped kept for the year</span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>
              {yearlyPerMonth}/mo
              {savingsPct > 0 ? ` · save ${savingsPct}%` : ""}
            </span>
          </span>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.planMonthSel : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        The stamp only marks how far your protection is paid through. Your
        photos never leave your vault, and nothing is ever deleted.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default PostmarkPaywall
